import { AdType } from '@tmap-web-lib/remote-api-client/frontman'
import { GetHomeQuizResponse } from '@tmap-web-lib/remote-api-client/frontman'
import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { formatCommaNumber } from '@tmap-web-lib/utils'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai/index'
import { debounce, sampleSize } from 'lodash-es'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import icPedometerLight from '@/assets/images/icon_color_pedometer_light.svg'
import { globalState } from '@/atoms'
import { sendEventLog } from '@/features/log/log.fn'
import MyLogDefinition from '@/features/log/log.my'
import { TodayQuizCardV1 } from '@/features/web/components/today-quiz-card-v1'
import { useOpenService } from '@/hooks'
import { useQuizCountdown } from '@/hooks/useQuizCountdown'
import type { StepCounterDashboardResponse } from '@/react-query/hooks/use-get-step-counter-dashboard'

import * as styles from './pointMissionBanner.css'

interface Props {
  isRedDot: boolean
  missionBannerData: AdType[] | undefined
  isLoading: boolean
  homeQuizData: GetHomeQuizResponse | null | undefined
  isFetched: boolean
  stepCounterData: StepCounterDashboardResponse | undefined
  showPedometer: boolean
  isPedometerActive: boolean | null
  isNewVersion?: boolean
}

function PointMissionBanner(props: Props) {
  const {
    isRedDot,
    missionBannerData,
    isLoading,
    homeQuizData,
    isFetched,
    stepCounterData,
    showPedometer,
    isPedometerActive,
    isNewVersion = false,
  } = props

  const scrollRef = useRef<HTMLDivElement>(null)
  const viewSurveyLogSent = useRef(false)
  const globalStateValue = useAtomValue(globalState)
  const missionTitle = globalStateValue.MY_MISSION ?? { title: '오늘의 미션', isShow: true }
  const stepCounterBannerText = globalStateValue.MY_STEP_COUNTER_BANNER
  const openService = useOpenService()
  const { action_id } = MyLogDefinition.my

  const [showArrow, setShowArrow] = useState(false)
  const [randomMissionBanner, setRandomMissionBanner] = useState<AdType[]>([])

  const { isExpired } = useQuizCountdown(homeQuizData?.endsDate ?? null)

  const showSurveyCard = useMemo(() => {
    if (!isFetched) return false
    if (!homeQuizData) return false
    if (homeQuizData.isParticipated) return false
    if (isExpired) return false
    return true
  }, [homeQuizData, isFetched, isExpired])

  const readRedDot = useCallback(() => {
    if (isRedDot) {
      TmapApp.updateRedDotList({
        updateData: [
          {
            itemGroup: 'SERVICE_INDEX',
            item: 'MY_MISSION',
            displayType: 'REDDOT',
            readDateTime: dayjs().format('YYYYMMDDHHmmss'),
          },
        ],
      })
      TmapApp.updateAllRedDots()
    }
  }, [isRedDot])

  const callClickDebouncing = useMemo(
    () =>
      debounce(
        (ad: AdType) => {
          if (ad.landingUrl) {
            sendEventLog(action_id?.tapMissionBanner, {
              adid: ad.adId,
            })
            readRedDot()
            openService(ad.landingUrl)
          }
        },
        1000,
        { leading: true, trailing: false }
      ),
    [readRedDot, openService, action_id?.tapMissionBanner]
  )

  const handleBannerClick = useCallback(
    (ad: AdType) => {
      callClickDebouncing(ad)
    },
    [callClickDebouncing]
  )

  const handleMissionAllClick = useCallback(() => {
    const adIds = missionBannerData && missionBannerData.map((data) => data.adId)
    sendEventLog(action_id?.tapMissionAll, { adid: adIds, unit: isRedDot ? 'REDDOT' : null })
    readRedDot()
    openService('tmap://paypoint?pageid=point&extra=%7B%22toref%22%3A%22mission%22%7D')
  }, [readRedDot, openService, action_id?.tapMissionAll, isRedDot, missionBannerData])

  const handleSurveyCardPress = useCallback(() => {
    sendEventLog(action_id?.tapSurveyQuiz, {
      id: homeQuizData?.displayId,
      title: homeQuizData?.question?.content,
      question_type: homeQuizData?.question?.type,
    })
    openService('tmap://vertical?service=survey')
  }, [openService, homeQuizData, action_id?.tapSurveyQuiz])

  const handleClickPedometerBanner = useCallback(() => {
    sendEventLog(action_id?.tapPedometer, {
      text: isPedometerActive === true ? '오늘 목표 화이팅' : (stepCounterBannerText?.normalText ?? null),
      cnt: isPedometerActive === true ? (stepCounterData?.todayActivity?.stepCounts ?? null) : null,
    })
    const extra = {
      logbox: {
        origin: 'my',
        type: 'banner',
      },
    }
    openService(
      `tmap://vertical?service=pedometer&extra=${encodeURIComponent(JSON.stringify(extra))}`
    )
  }, [openService, action_id?.tapPedometer, isPedometerActive, stepCounterBannerText, stepCounterData])

  const displayMissionBanner = useCallback(() => {
    if (!isLoading && randomMissionBanner && randomMissionBanner.length) {
      if (isNewVersion) {
        return (
          <>
            {randomMissionBanner.map((banner) => {
              const bannerImgUrl = banner.materials.find((item) => item.type === 'image' && item.url)?.url
              return (
                <button
                  key={banner.adId}
                  className={styles.missionListRow}
                  onClick={() => handleBannerClick(banner)}
                >
                  {bannerImgUrl && (
                    <Image
                      className={styles.missionListImg}
                      src={bannerImgUrl}
                      alt={''}
                      width={280}
                      height={58}
                      unoptimized
                    />
                  )}
                  <Image
                    src={'/assets/images/ico_arrow_right.svg'}
                    unoptimized
                    alt=""
                    width={16}
                    height={16}
                    className={styles.arrowIcon}
                  />
                </button>
              )
            })}
          </>
        )
      }

      if (randomMissionBanner.length === 1) {
        const bannerImgUrl = randomMissionBanner[0].materials.find((item) => item.type === 'image' && item.url)?.url

        return (
          <button
            className={styles.missionItem}
            onClick={() => handleBannerClick(randomMissionBanner[0])}
          >
            {bannerImgUrl && (
              <Image
                className={styles.missionImg}
                src={bannerImgUrl}
                alt={''}
                width={280}
                height={73}
                unoptimized
              />
            )}
            <Image
              src={'/assets/images/ico_arrow_right.svg'}
              unoptimized
              alt=""
              width={16}
              height={16}
              className={styles.arrowIcon}
            />
          </button>
        )
      } else {
        return (
          <div
            ref={scrollRef}
            className={classNames(
              styles.bannerWrapper,
              showArrow ? styles.bannerWrapperVariants.showArrowBtn : ''
            )}
          >
            {randomMissionBanner.map((banner) => {
              const bannerImgUrl = banner.materials.find((item) => item.type === 'image' && item.url)?.url
              return (
                <button
                  key={banner.adId}
                  onClick={() => handleBannerClick(banner)}
                  className={styles.bannerImgBtn}
                >
                  {bannerImgUrl && (
                    <Image
                      className={styles.bannerImgCompact}
                      src={bannerImgUrl}
                      alt={''}
                      width={320}
                      height={84}
                      unoptimized
                    />
                  )}
                </button>
              )
            })}
            {showArrow && (
              <button
                className={styles.arrowSignWrapper}
                onClick={handleMissionAllClick}
              >
                <Image
                  src={require('../../../../assets/images/arrow_tale_right.svg').default}
                  alt=""
                  className={styles.arrowSign}
                />
              </button>
            )}
          </div>
        )
      }
    }
  }, [
    isLoading,
    isNewVersion,
    randomMissionBanner,
    handleBannerClick,
    handleMissionAllClick,
    showArrow,
  ])

  useEffect(() => {
    if (missionBannerData) {
      const imageBanners = missionBannerData.filter((ad) =>
        ad.materials.some((item) => item.type === 'image' && item.url)
      )
      if (imageBanners.length >= 3) {
        const randomItems = sampleSize(imageBanners, 3)
        setRandomMissionBanner(randomItems)
      } else {
        setRandomMissionBanner(imageBanners)
      }
    }
  }, [missionBannerData])

  useEffect(() => {
    if (missionBannerData && missionBannerData.length > 3) {
      setShowArrow(true)
    }
  }, [missionBannerData])

  useEffect(() => {
    if (missionTitle.isShow && randomMissionBanner.length > 0) {
      const adIds = randomMissionBanner.map((data) => data.adId)
      sendEventLog(action_id?.viewMission, { adid: adIds, unit: isRedDot ? 'REDDOT' : null })
    }
  }, [action_id?.viewMission, isRedDot, randomMissionBanner, missionTitle.isShow])

  useEffect(() => {
    if (!showSurveyCard || !homeQuizData || viewSurveyLogSent.current) return
    viewSurveyLogSent.current = true
    sendEventLog(action_id?.viewSurveyQuiz, {
      id: homeQuizData.displayId,
      title: homeQuizData.question?.content,
      question_type: homeQuizData.question?.type,
    })
  }, [showSurveyCard, homeQuizData, action_id?.viewSurveyQuiz])

  if (!missionTitle.isShow) {
    return null
  } else if (!missionBannerData && !showSurveyCard && !(showPedometer && stepCounterData)) {
    return null
  }

  if (!isNewVersion) {
    return (
      <>
        <div className={styles.titleWrapper}>
          <div className={styles.title}>
            <p className={`${isRedDot ? styles.redDotVariants.reddot : ''}`}>{missionTitle.title}</p>
            <button className={styles.btn_more_wrapper} onClick={handleMissionAllClick}>
              <p className={styles.btn_more}>전체보기</p>
            </button>
          </div>
        </div>
        {showSurveyCard && homeQuizData && (
          <div style={{ padding: '0 20px 16px' }}>
            <TodayQuizCardV1
              homeQuizData={homeQuizData}
              onPress={handleSurveyCardPress}
            />
          </div>
        )}
        <div style={{ position: 'relative', display: 'flex' }}>
          <div>{displayMissionBanner()}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.cardV2}>
        <p className={styles.cardTitle}>오늘의 미션</p>
        {showPedometer && stepCounterData && (
          <button
            className={styles.pedometerRow}
            onClick={handleClickPedometerBanner}
          >
            {isPedometerActive === true ? (
              <>
                <Image
                  className={styles.pedometerIcon}
                  src={icPedometerLight}
                  alt=""
                  unoptimized
                />
                <div className={styles.pedometerContent}>
                  <p className={styles.pedometerTitle}>
                    만보기 <span className={styles.pedometerBold}>{formatCommaNumber(stepCounterData.todayActivity?.stepCounts ?? 0)}걸음</span>
                  </p>
                  <p className={styles.pedometerSubtitle}>오늘 목표 화이팅!</p>
                </div>
              </>
            ) : isPedometerActive === false ? (
              <>
                <Image
                  className={styles.pedometerIcon}
                  src={icPedometerLight}
                  alt=""
                  unoptimized
                />
                <div className={styles.pedometerContent}>
                  <p className={styles.pedometerTitle}>
                    {stepCounterBannerText?.boldText ?? '만보기로 매일 포인트 받기'}
                  </p>
                  <p className={styles.pedometerSubtitle}>
                    {stepCounterBannerText?.normalText ?? '출석만 해도 포인트를 드려요!'}
                  </p>
                </div>
              </>
            ) : null}
            <Image
              src={'/assets/images/ico_arrow_right.svg'}
              unoptimized
              alt=""
              width={16}
              height={16}
              className={styles.arrowIcon}
            />
          </button>
        )}
        {displayMissionBanner()}
        <button className={styles.moreBtn} onClick={handleMissionAllClick}>
          오늘의 미션 바로가기
          <Image
            src={'/assets/images/ico_arrow_right.svg'}
            unoptimized
            alt=""
            width={16}
            height={16}
          />
        </button>
      </div>
    </>
  )
}

export { PointMissionBanner }
